import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { badgeClasses } from '@/utils/badge_classes'

// Drives the Team Playbook "saved views" menu: save the current filters as a
// named view, rename one, update an active view's filters, or delete it.
// Applying a view is a plain link (Turbo navigates) — no JS needed for that.
export default class extends Controller {
  static targets = ["modal", "modalTitle", "nameInput", "error", "filtersBlock", "filterBadges", "defaultInput", "submitButton", "deleteButton", "sortable"]
  static values = { createUrl: String, activeId: String, currentFilters: Array, renameId: String, renameName: String }

  // "Create" vs "rename" mode is derived from whether a view is being edited,
  // rather than tracked as a separate flag that could drift out of sync.
  get isRename() {
    return this.renameIdValue !== ""
  }

  connect() {
    this.#initSortable()
  }

  disconnect() {
    this.sortable?.destroy()
  }

  openCreate(event) {
    event.preventDefault()
    this.renameIdValue = ""
    this.renameNameValue = ""
    this.modalTitleTarget.textContent = "Save view"
    this.submitButtonTarget.textContent = "Save view"
    this.filtersBlockTarget.classList.remove("d-none")
    this.#renderFilterBadges(this.currentFiltersValue)
    this.defaultInputTarget.closest(".form-check").classList.remove("d-none")
    this.deleteButtonTarget.classList.add("d-none")
    this.nameInputTarget.value = ""
    this.defaultInputTarget.checked = false
    this.#showModal()
  }

  openRename(event) {
    event.preventDefault()
    const { viewId, viewName, viewDefault, viewFilters } = event.currentTarget.dataset
    this.renameIdValue = viewId
    this.renameNameValue = viewName
    this.modalTitleTarget.textContent = "Edit view"
    this.submitButtonTarget.textContent = "Save name"
    // Filters aren't editable here, but show them so the user knows which view
    // they're editing.
    this.filtersBlockTarget.classList.remove("d-none")
    this.#renderFilterBadges(this.#parseFilters(viewFilters))
    this.defaultInputTarget.closest(".form-check").classList.remove("d-none")
    this.deleteButtonTarget.classList.remove("d-none")
    this.nameInputTarget.value = viewName || ""
    this.defaultInputTarget.checked = viewDefault === "true"
    this.#showModal()
  }

  submit(event) {
    event.preventDefault()
    const name = this.nameInputTarget.value.trim()
    if (!name) return this.#showError("Enter a name")

    const isRename = this.isRename
    const url = isRename ? `${this.createUrlValue}/${this.renameIdValue}` : this.createUrlValue
    const body = { saved_form_view: { name, is_default: this.defaultInputTarget.checked } }
    if (!isRename) body.saved_form_view.search_params = this.#currentFilters()

    this.#request(url, isRename ? "PATCH" : "POST", body)
      .then((data) => {
        this.#hideModal()
        if (isRename) {
          this.#reload()
        } else {
          this.#applyView(data)
        }
      })
      .catch((messages) => this.#showError(messages))
  }

  updateActive(event) {
    event.preventDefault()
    if (!this.activeIdValue) return
    const url = `${this.createUrlValue}/${this.activeIdValue}`
    this.#request(url, "PATCH", { saved_form_view: { search_params: this.#currentFilters() } })
      .then(() => this.#reload())
      .catch((messages) => alert(messages))
  }

  // Delete is only reachable from the edit modal, acting on the view being
  // edited.
  confirmDelete(event) {
    event.preventDefault()
    if (!this.isRename) return
    if (!window.confirm(`Delete the saved view "${this.renameNameValue}"?`)) return

    this.#request(`${this.createUrlValue}/${this.renameIdValue}`, "DELETE")
      .then(() => {
        this.#hideModal()
        if (this.renameIdValue === this.activeIdValue) {
          Turbo.visit(window.location.pathname)
        } else {
          this.#reload()
        }
      })
      .catch(() => alert("Failed to delete view"))
  }

  // Read the live filters straight off the URL, dropping non-filter params.
  #currentFilters() {
    const params = new URLSearchParams(window.location.search)
    const filters = {}
    for (const [key, value] of params.entries()) {
      const base = key.replace(/\[\]$/, "")
      if (base === "page" || base === "saved_view" || value === "") continue
      if (key.endsWith("[]")) {
        ;(filters[base] ||= []).push(value)
      } else {
        filters[base] = value
      }
    }
    return filters
  }

  #applyView(view) {
    const params = new URLSearchParams()
    Object.entries(view.search_params || {}).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(`${key}[]`, v))
      } else {
        params.set(key, value)
      }
    })
    params.set("saved_view", view.id)
    Turbo.visit(`${window.location.pathname}?${params.toString()}`)
  }

  #reload() {
    Turbo.visit(window.location.href, { action: "replace" })
  }

  #request(url, method, body) {
    const options = {
      method,
      headers: { "X-CSRF-Token": this.#csrfToken(), Accept: "application/json" },
    }
    if (body) {
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify(body)
    }
    return fetch(url, options).then((response) => {
      // A followed redirect (expired session, lost access) lands on an HTML page
      // with an ok status; a feature-flag 404 has no body at all. Neither is
      // JSON, so fall back to a readable message instead of a parse error.
      if (response.ok && !response.redirected) {
        return method === "DELETE" || response.status === 204 ? {} : response.json()
      }
      return response.json().catch(() => ({})).then((data) => {
        throw (data.errors || [response.redirected ? "Your session has expired. Reload the page and try again." : "Something went wrong"]).join(", ")
      })
    })
  }

  // Drag-and-drop reordering of the saved-view rows. The grip handle starts the
  // drag; on drop we persist the new order by sending the row ids top-to-bottom.
  #initSortable() {
    if (!this.hasSortableTarget) return

    this.sortable = Sortable.create(this.sortableTarget, {
      animation: 150,
      handle: ".saved-view-handle",
      ghostClass: "sortable-ghost",
      dragClass: "sortable-drag",
      onEnd: (event) => {
        if (event.newIndex === event.oldIndex) return
        this.#persistOrder()
      },
    })
  }

  #persistOrder() {
    const ids = Array.from(this.sortableTarget.querySelectorAll("[data-saved-view-row]"))
      .map((row) => row.dataset.savedViewId)

    this.#request(`${this.createUrlValue}/reorder`, "PATCH", { ids }).catch(() => this.#reload())
  }

  #parseFilters(json) {
    try {
      return JSON.parse(json || "[]")
    } catch {
      return []
    }
  }

  // Render the filter summary payloads ({ text, title? }) as light badges.
  // Badges wrap inside the modal, and truncated value lists keep the full
  // list reachable via their tooltip.
  #renderFilterBadges(items) {
    const container = this.filterBadgesTarget
    container.replaceChildren()

    if (!items || items.length === 0) {
      const empty = document.createElement("span")
      empty.className = "small text-muted"
      empty.textContent = "No filters — shows all rows."
      container.appendChild(empty)
      return
    }

    items.forEach((item) => {
      const badge = document.createElement("span")
      badge.className = badgeClasses({ variant: 'light', outline: true, extra: 'text-wrap text-start text-break mw-100' })
      badge.textContent = item.text
      if (item.title) badge.title = item.title
      container.appendChild(badge)
    })
  }

  #showError(message) {
    this.nameInputTarget.classList.add("is-invalid")
    this.errorTarget.textContent = message
  }

  #showModal() {
    this.nameInputTarget.classList.remove("is-invalid")
    this.errorTarget.textContent = ""
    this.modal = window.bootstrap.Modal.getOrCreateInstance(this.modalTarget)
    this.modal.show()
  }

  #hideModal() {
    window.bootstrap.Modal.getInstance(this.modalTarget)?.hide()
  }

  #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
